import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Button,
  Select,
  Skeleton,
  Empty,
  Tag,
  Popconfirm,
  Input,
  InputNumber,
  Modal,
  message,
  Row,
  Col,
  Space,
} from 'antd';
// Spin دیگر استفاده نمی‌شود - از Skeleton استفاده می‌کنیم
import { 
  DollarOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  ToolOutlined,
  TeamOutlined,
  SettingOutlined,
  UserOutlined,
  AppstoreOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import { actionApi, ActionValidationError } from '../../../utils/actionApi';
import { getSuccessMessage } from '../../../utils/actionMessageTranslator';
import { numberFormatter, numberParser } from '../../../utils/numberUtils';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function ActionResourcesTab({ actionId, primaryButtonClass }) {
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesSummary, setResourcesSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [resourceTypeFilter, setResourceTypeFilter] = useState(undefined);

  // فرم ایجاد/ویرایش
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [editingResourceId, setEditingResourceId] = useState(null);
  const [resourceType, setResourceType] = useState(undefined);
  const [resourceDescription, setResourceDescription] = useState('');
  const [resourceStatus, setResourceStatus] = useState('planned');
  const [resourceSubmitting, setResourceSubmitting] = useState(false);
  
  // Dynamic fields برای بودجه
  const [budgetAmount, setBudgetAmount] = useState('');
  const [budgetCurrency, setBudgetCurrency] = useState('');
  const [budgetCategory, setBudgetCategory] = useState('');
  const [budgetFiscalYear, setBudgetFiscalYear] = useState('');
  const [budgetAllocatedAmount, setBudgetAllocatedAmount] = useState('');
  const [budgetUsedAmount, setBudgetUsedAmount] = useState('');
  
  // Dynamic fields برای نیروی انسانی
  const [personCount, setPersonCount] = useState('');
  const [personHours, setPersonHours] = useState('');
  const [personSkillLevel, setPersonSkillLevel] = useState('');
  const [personPosition, setPersonPosition] = useState('');
  const [personDepartment, setPersonDepartment] = useState('');
  const [personAllocatedAmount, setPersonAllocatedAmount] = useState('');
  const [personUsedAmount, setPersonUsedAmount] = useState('');
  
  // Dynamic fields برای تجهیزات
  const [equipmentName, setEquipmentName] = useState('');
  const [equipmentQuantity, setEquipmentQuantity] = useState('');
  const [equipmentModel, setEquipmentModel] = useState('');
  const [equipmentSpecifications, setEquipmentSpecifications] = useState('');
  const [equipmentCondition, setEquipmentCondition] = useState('');
  const [equipmentAllocatedAmount, setEquipmentAllocatedAmount] = useState('');
  const [equipmentUsedAmount, setEquipmentUsedAmount] = useState('');
  
  // Dynamic fields برای ابزار
  const [toolName, setToolName] = useState('');
  const [toolQuantity, setToolQuantity] = useState('');
  const [toolCondition, setToolCondition] = useState('');
  const [toolSpecifications, setToolSpecifications] = useState('');
  const [toolAllocatedAmount, setToolAllocatedAmount] = useState('');
  const [toolUsedAmount, setToolUsedAmount] = useState('');
  
  // Dynamic fields برای پیمانکار
  const [contractorName, setContractorName] = useState('');
  const [contractValue, setContractValue] = useState('');
  const [contractDuration, setContractDuration] = useState('');
  const [contractDurationUnit, setContractDurationUnit] = useState(undefined);
  const [contractType, setContractType] = useState('');
  const [contractorAllocatedAmount, setContractorAllocatedAmount] = useState('');
  const [contractorUsedAmount, setContractorUsedAmount] = useState('');
  
  // Dynamic fields برای سایر
  const [customFields, setCustomFields] = useState([]);
  const [otherAllocatedAmount, setOtherAllocatedAmount] = useState('');
  const [otherUsedAmount, setOtherUsedAmount] = useState('');

  const loadResources = async () => {
    if (!actionId) return;
    setResourcesLoading(true);
    setSummaryLoading(true);
    try {
      const res = await actionApi.getActionResources(actionId, { type: resourceTypeFilter || undefined });
      // فرمت جدید: data.items برای لیست منابع
      const items = res?.data?.items || res?.data?.data || res?.data || [];
      setResources(Array.isArray(items) ? items : []);
      
      // استفاده از aggregations برای summary
      const aggregations = res?.data?.aggregations || {};
      if (aggregations && Object.keys(aggregations).length > 0) {
        const summary = {
          total_resources: aggregations.by_type 
            ? Object.values(aggregations.by_type).reduce((sum, type) => sum + (type.count || 0), 0)
            : items.length,
          total_budget: aggregations.total_amount || 0,
          by_type: aggregations.by_type || {},
        };
        setResourcesSummary(summary);
      } else {
        // اگر aggregations نبود، از endpoint جداگانه استفاده می‌کنیم
        try {
          const summaryRes = await actionApi.getActionResourcesSummary(actionId);
          const summary = summaryRes?.data || {};
          setResourcesSummary(summary);
        } catch (summaryError) {
          console.error('Load summary error:', summaryError);
          // اگر summary endpoint هم کار نکرد، از داده‌های موجود استفاده می‌کنیم
          const summary = {
            total_resources: items.length,
            total_budget: 0,
            by_type: {},
          };
          setResourcesSummary(summary);
        }
      }
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت منابع');
    } finally {
      setResourcesLoading(false);
      setSummaryLoading(false);
    }
  };

  const loadResourcesSummary = async () => {
    // این تابع دیگر استفاده نمی‌شود چون summary از loadResources می‌آید
    // اما برای سازگاری با کدهای دیگر نگه داشته شده است
    if (!actionId) return;
    setSummaryLoading(true);
    try {
      const res = await actionApi.getActionResourcesSummary(actionId);
      const summary = res?.data || {};
      setResourcesSummary(summary);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت خلاصه منابع');
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    loadResources();
    // loadResourcesSummary دیگر نیاز نیست چون در loadResources انجام می‌شود
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId, resourceTypeFilter]);

  const resetAllFields = () => {
    // فقط فیلدهای dynamic را reset می‌کنیم، نه resourceType را
    // بودجه
    setBudgetAmount('');
    setBudgetCurrency('');
    setBudgetCategory('');
    setBudgetFiscalYear('');
    setBudgetAllocatedAmount('');
    setBudgetUsedAmount('');
    
    // نیروی انسانی
    setPersonCount('');
    setPersonHours('');
    setPersonSkillLevel('');
    setPersonPosition('');
    setPersonDepartment('');
    setPersonAllocatedAmount('');
    setPersonUsedAmount('');
    
    // تجهیزات
    setEquipmentName('');
    setEquipmentQuantity('');
    setEquipmentModel('');
    setEquipmentSpecifications('');
    setEquipmentCondition('');
    setEquipmentAllocatedAmount('');
    setEquipmentUsedAmount('');
    
    // ابزار
    setToolName('');
    setToolQuantity('');
    setToolCondition('');
    setToolSpecifications('');
    setToolAllocatedAmount('');
    setToolUsedAmount('');
    
    // پیمانکار
    setContractorName('');
    setContractValue('');
    setContractDuration('');
    setContractDurationUnit(undefined);
    setContractType('');
    setContractorAllocatedAmount('');
    setContractorUsedAmount('');
    
    // سایر
    setCustomFields([]);
    setOtherAllocatedAmount('');
    setOtherUsedAmount('');
  };

  const resetAllFieldsAndType = () => {
    setEditingResourceId(null);
    setResourceType(undefined);
    setResourceDescription('');
    setResourceStatus('planned');
    resetAllFields();
  };

  const openCreateModal = () => {
    resetAllFieldsAndType();
    setResourceModalOpen(true);
  };

  const openEditModal = async (resourceId) => {
    setResourceSubmitting(true);
    try {
      const res = await actionApi.getActionResource(actionId, resourceId);
      const resource = res?.data || {};
      const dynamicFields = resource.dynamic_fields || {};
      
      setEditingResourceId(resourceId);
      setResourceType(resource.resource_type);
      setResourceDescription(resource.description || '');
      setResourceStatus(resource.status || 'planned');
      
      // پر کردن فیلدهای dynamic بر اساس نوع منبع
      if (resource.resource_type === 'بودجه') {
        setBudgetAmount(dynamicFields.amount || '');
        setBudgetCurrency(dynamicFields.currency || '');
        setBudgetCategory(dynamicFields.budget_category || '');
        setBudgetFiscalYear(dynamicFields.fiscal_year || '');
        setBudgetAllocatedAmount(dynamicFields.allocated_amount || '');
        setBudgetUsedAmount(dynamicFields.used_amount || '');
      } else if (resource.resource_type === 'نیروی_انسانی') {
        setPersonCount(dynamicFields.person_count || '');
        setPersonHours(dynamicFields.hours || '');
        setPersonSkillLevel(dynamicFields.skill_level || '');
        setPersonPosition(dynamicFields.position || '');
        setPersonDepartment(dynamicFields.department || '');
        setPersonAllocatedAmount(dynamicFields.allocated_amount || '');
        setPersonUsedAmount(dynamicFields.used_amount || '');
      } else if (resource.resource_type === 'تجهیزات') {
        setEquipmentName(dynamicFields.equipment_name || '');
        setEquipmentQuantity(dynamicFields.quantity || '');
        setEquipmentModel(dynamicFields.model || '');
        setEquipmentSpecifications(dynamicFields.specifications || '');
        setEquipmentCondition(dynamicFields.condition || '');
        setEquipmentAllocatedAmount(dynamicFields.allocated_amount || '');
        setEquipmentUsedAmount(dynamicFields.used_amount || '');
      } else if (resource.resource_type === 'ابزار') {
        setToolName(dynamicFields.tool_name || '');
        setToolQuantity(dynamicFields.quantity || '');
        setToolCondition(dynamicFields.condition || '');
        setToolSpecifications(dynamicFields.specifications || '');
        setToolAllocatedAmount(dynamicFields.allocated_amount || '');
        setToolUsedAmount(dynamicFields.used_amount || '');
      } else if (resource.resource_type === 'پیمانکار') {
        setContractorName(dynamicFields.contractor_name || '');
        setContractValue(dynamicFields.contract_value || '');
        setContractDuration(dynamicFields.contract_duration || '');
        setContractDurationUnit(dynamicFields.contract_duration_unit || undefined);
        setContractType(dynamicFields.contract_type || '');
        setContractorAllocatedAmount(dynamicFields.allocated_amount || '');
        setContractorUsedAmount(dynamicFields.used_amount || '');
      } else if (resource.resource_type === 'سایر') {
        setCustomFields(Array.isArray(dynamicFields.custom_fields) ? dynamicFields.custom_fields : []);
        setOtherAllocatedAmount(dynamicFields.allocated_amount || '');
        setOtherUsedAmount(dynamicFields.used_amount || '');
      }
      
      setResourceModalOpen(true);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت اطلاعات منبع');
    } finally {
      setResourceSubmitting(false);
    }
  };

  const closeResourceModal = () => {
    setResourceModalOpen(false);
    resetAllFieldsAndType();
  };

  const buildDynamicFields = () => {
    const dynamicFields = {};
    
    if (resourceType === 'بودجه') {
      if (!budgetAmount || Number(budgetAmount) <= 0) {
        message.error('مبلغ بودجه الزامی است و باید عددی مثبت باشد');
        return null;
      }
      if (!budgetCurrency) {
        message.error('واحد پول الزامی است');
        return null;
      }
      dynamicFields.amount = parseFloat(budgetAmount);
      dynamicFields.currency = budgetCurrency;
      if (budgetCategory) dynamicFields.budget_category = budgetCategory;
      if (budgetFiscalYear) dynamicFields.fiscal_year = parseInt(budgetFiscalYear);
      if (budgetAllocatedAmount) dynamicFields.allocated_amount = parseFloat(budgetAllocatedAmount);
      if (budgetUsedAmount) dynamicFields.used_amount = parseFloat(budgetUsedAmount);
    } else if (resourceType === 'نیروی_انسانی') {
      if (!personCount || Number(personCount) < 1) {
        message.error('تعداد نفرات الزامی است و باید حداقل 1 باشد');
        return null;
      }
      dynamicFields.person_count = parseInt(personCount);
      if (personHours) dynamicFields.hours = parseFloat(personHours);
      if (personSkillLevel) dynamicFields.skill_level = personSkillLevel;
      if (personPosition) dynamicFields.position = personPosition;
      if (personDepartment) dynamicFields.department = personDepartment;
      if (personAllocatedAmount) dynamicFields.allocated_amount = parseFloat(personAllocatedAmount);
      if (personUsedAmount) dynamicFields.used_amount = parseFloat(personUsedAmount);
    } else if (resourceType === 'تجهیزات') {
      if (!equipmentName) {
        message.error('نام تجهیزات الزامی است');
        return null;
      }
      if (!equipmentQuantity || Number(equipmentQuantity) < 1) {
        message.error('تعداد تجهیزات الزامی است و باید حداقل 1 باشد');
        return null;
      }
      dynamicFields.equipment_name = equipmentName;
      dynamicFields.quantity = parseInt(equipmentQuantity);
      if (equipmentModel) dynamicFields.model = equipmentModel;
      if (equipmentSpecifications) dynamicFields.specifications = equipmentSpecifications;
      if (equipmentCondition) dynamicFields.condition = equipmentCondition;
      if (equipmentAllocatedAmount) dynamicFields.allocated_amount = parseFloat(equipmentAllocatedAmount);
      if (equipmentUsedAmount) dynamicFields.used_amount = parseFloat(equipmentUsedAmount);
    } else if (resourceType === 'ابزار') {
      if (!toolName) {
        message.error('نام ابزار الزامی است');
        return null;
      }
      if (!toolQuantity || Number(toolQuantity) < 1) {
        message.error('تعداد ابزار الزامی است و باید حداقل 1 باشد');
        return null;
      }
      dynamicFields.tool_name = toolName;
      dynamicFields.quantity = parseInt(toolQuantity);
      if (toolCondition) dynamicFields.condition = toolCondition;
      if (toolSpecifications) dynamicFields.specifications = toolSpecifications;
      if (toolAllocatedAmount) dynamicFields.allocated_amount = parseFloat(toolAllocatedAmount);
      if (toolUsedAmount) dynamicFields.used_amount = parseFloat(toolUsedAmount);
    } else if (resourceType === 'پیمانکار') {
      if (!contractorName) {
        message.error('نام پیمانکار الزامی است');
        return null;
      }
      if (!contractValue || Number(contractValue) <= 0) {
        message.error('مبلغ قرارداد الزامی است و باید عددی مثبت باشد');
        return null;
      }
      dynamicFields.contractor_name = contractorName;
      dynamicFields.contract_value = parseFloat(contractValue);
      if (contractDuration) dynamicFields.contract_duration = parseInt(contractDuration);
      if (contractDurationUnit) dynamicFields.contract_duration_unit = contractDurationUnit;
      if (contractType) dynamicFields.contract_type = contractType;
      if (contractorAllocatedAmount) dynamicFields.allocated_amount = parseFloat(contractorAllocatedAmount);
      if (contractorUsedAmount) dynamicFields.used_amount = parseFloat(contractorUsedAmount);
    } else if (resourceType === 'سایر') {
      if (customFields.length > 0) {
        dynamicFields.custom_fields = customFields.filter(cf => cf.key && cf.value);
      }
      if (otherAllocatedAmount) dynamicFields.allocated_amount = parseFloat(otherAllocatedAmount);
      if (otherUsedAmount) dynamicFields.used_amount = parseFloat(otherUsedAmount);
    }
    
    return dynamicFields;
  };

  const onSubmitResource = async () => {
    if (!resourceType) {
      message.error('نوع منبع الزامی است');
      return;
    }

    const dynamicFields = buildDynamicFields();
    if (dynamicFields === null) {
      return; // خطا در اعتبارسنجی
    }

    setResourceSubmitting(true);
    try {
      const payload = {
        resource_type: resourceType,
        dynamic_fields: dynamicFields,
      };
      
      if (resourceDescription) payload.description = resourceDescription;
      if (resourceStatus) payload.status = resourceStatus;

      if (editingResourceId) {
        const res = await actionApi.updateActionResource(actionId, editingResourceId, payload);
        if (res?.success) {
          message.success(getSuccessMessage(res.message, 'منبع به‌روزرسانی شد'));
          closeResourceModal();
          await loadResources();
        } else {
          message.error(res?.message || 'خطا در به‌روزرسانی منبع');
        }
      } else {
        const res = await actionApi.createActionResource(actionId, payload);
        if (res?.success) {
          message.success(getSuccessMessage(res.message, 'منبع ایجاد شد'));
          closeResourceModal();
          await loadResources();
        } else {
          message.error(res?.message || 'خطا در ایجاد منبع');
        }
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        message.error(error.message || 'خطا در اعتبارسنجی منبع');
      } else {
        message.error(error.message || 'خطا در ثبت منبع');
      }
    } finally {
      setResourceSubmitting(false);
    }
  };

  const onDeleteResource = async (resourceId) => {
    try {
      const res = await actionApi.deleteActionResource(actionId, resourceId);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'منبع حذف شد'));
        await loadResources();
      } else {
        message.error(res?.message || 'خطا در حذف منبع');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در حذف منبع');
    }
  };

  // لیست انواع منابع با آیکون
  const resourceTypeOptions = [
    { 
      value: 'بودجه', 
      label: (
        <Space>
          <DollarOutlined style={{ color: '#10b981' }} />
          <span>بودجه</span>
        </Space>
      )
    },
    { 
      value: 'ابزار', 
      label: (
        <Space>
          <ToolOutlined style={{ color: '#f59e0b' }} />
          <span>ابزار</span>
        </Space>
      )
    },
    { 
      value: 'نیروی_انسانی', 
      label: (
        <Space>
          <TeamOutlined style={{ color: '#3b82f6' }} />
          <span>نیروی انسانی</span>
        </Space>
      )
    },
    { 
      value: 'تجهیزات', 
      label: (
        <Space>
          <SettingOutlined style={{ color: '#8b5cf6' }} />
          <span>تجهیزات</span>
        </Space>
      )
    },
    { 
      value: 'پیمانکار', 
      label: (
        <Space>
          <UserOutlined style={{ color: '#ec4899' }} />
          <span>پیمانکار</span>
        </Space>
      )
    },
    { 
      value: 'سایر', 
      label: (
        <Space>
          <AppstoreOutlined style={{ color: '#6b7280' }} />
          <span>سایر</span>
        </Space>
      )
    },
  ];

  const getResourceTypeLabel = (type) => {
    const found = resourceTypeOptions.find(opt => opt.value === type);
    return found ? found.label : type;
  };

  // آیکون و رنگ برای هر نوع منبع
  const getResourceTypeConfig = (type) => {
    const configs = {
      'بودجه': { 
        icon: <DollarOutlined />, 
        color: '#10b981', 
        bgColor: '#ecfdf5',
        darkBgColor: 'rgba(16, 185, 129, 0.1)'
      },
      'ابزار': { 
        icon: <ToolOutlined />, 
        color: '#f59e0b', 
        bgColor: '#fffbeb',
        darkBgColor: 'rgba(245, 158, 11, 0.1)'
      },
      'نیروی_انسانی': { 
        icon: <TeamOutlined />, 
        color: '#3b82f6', 
        bgColor: '#eff6ff',
        darkBgColor: 'rgba(59, 130, 246, 0.1)'
      },
      'تجهیزات': { 
        icon: <SettingOutlined />, 
        color: '#8b5cf6', 
        bgColor: '#f5f3ff',
        darkBgColor: 'rgba(139, 92, 246, 0.1)'
      },
      'پیمانکار': { 
        icon: <UserOutlined />, 
        color: '#ec4899', 
        bgColor: '#fdf2f8',
        darkBgColor: 'rgba(236, 72, 153, 0.1)'
      },
      'سایر': { 
        icon: <AppstoreOutlined />, 
        color: '#6b7280', 
        bgColor: '#f9fafb',
        darkBgColor: 'rgba(107, 114, 128, 0.1)'
      },
    };
    return configs[type] || { 
      icon: <DatabaseOutlined />, 
      color: '#6b7280', 
      bgColor: '#f9fafb',
      darkBgColor: 'rgba(107, 114, 128, 0.1)'
    };
  };

  return (
    <>
      <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 16 }}>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
              منابع اقدام
            </Title>
            {/* <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
              مدیریت منابع مورد نیاز برای این اقدام (بودجه، زمان، مواد، تجهیزات و ...)
            </Text> */}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select
              size="middle"
              allowClear
              placeholder="فیلتر نوع"
              value={resourceTypeFilter}
              onChange={(val) => {
                setResourceTypeFilter(val);
                setTimeout(() => {
                  loadResources();
                }, 0);
              }}
              style={{ minWidth: 140, borderRadius: 10, fontFamily: 'CustomFont' }}
              options={resourceTypeOptions}
            />
            <Button
              onClick={() => {
                loadResources();
              }}
              disabled={resourcesLoading || summaryLoading}
              style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
            >
              به‌روزرسانی
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
              className={primaryButtonClass}
              style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
            >
              افزودن منبع
            </Button>
          </div>
        </div>

        {/* خلاصه منابع */}
        {summaryLoading ? (
          <div className="mt-4">
            {/* اسکلتون کارت کلی */}
            <div className="mb-4 p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Skeleton.Avatar active size={48} shape="square" style={{ borderRadius: 12 }} />
                  <div>
                    <Skeleton.Input active size="small" style={{ width: 80, height: 14, marginBottom: 8 }} />
                    <Skeleton.Input active size="default" style={{ width: 100, height: 28 }} />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Skeleton.Avatar active size={48} shape="square" style={{ borderRadius: 12 }} />
                  <div>
                    <Skeleton.Input active size="small" style={{ width: 80, height: 14, marginBottom: 8 }} />
                    <Skeleton.Input active size="default" style={{ width: 100, height: 28 }} />
                  </div>
                </div>
              </div>
            </div>

            {/* اسکلتون کاردهای نوع منابع */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <Card
                  key={i}
                  size="small"
                  style={{ borderRadius: 16, overflow: 'hidden' }}
                  bodyStyle={{ padding: 0 }}
                >
                  <div className="p-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Skeleton.Avatar active size={32} shape="square" style={{ borderRadius: 8 }} />
                      <Skeleton.Input active size="small" style={{ width: 60, height: 16 }} />
                    </div>
                  </div>
                  <div className="p-3">
                    <Skeleton.Input active size="default" style={{ width: 70, height: 24, marginBottom: 8 }} />
                    <Skeleton.Input active size="small" style={{ width: 80, height: 12 }} />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ) : resourcesSummary && resourcesSummary.by_type && Object.keys(resourcesSummary.by_type).length > 0 ? (
          <div className="mt-4">
            {/* کارت کلی */}
            <div className="mb-4 p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-100 dark:border-blue-800">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
                    <DatabaseOutlined style={{ fontSize: 24, color: '#fff' }} />
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
                      مجموع منابع
                    </Text>
                    <div>
                      <Text style={{ fontFamily: 'CustomFont', fontSize: 24, fontWeight: 700 }}>
                        {resourcesSummary.total_resources?.toLocaleString('fa-IR') || 0}
                      </Text>
                      <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 14, marginRight: 4 }}>
                        مورد
                      </Text>
                    </div>
                  </div>
                </div>
                {resourcesSummary.total_budget !== undefined && (
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center">
                      <DollarOutlined style={{ fontSize: 24, color: '#fff' }} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
                        مجموع بودجه
                      </Text>
                      <div>
                        <Text style={{ fontFamily: 'CustomFont', fontSize: 24, fontWeight: 700, color: '#10b981' }}>
                          {resourcesSummary.total_budget?.toLocaleString('fa-IR') || 0}
                        </Text>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* کاردهای نوع منابع */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(resourcesSummary.by_type).map(([type, data]) => {
                const config = getResourceTypeConfig(type);
                return (
                  <Card
                    key={type}
                    size="small"
                    className="hover:shadow-md transition-shadow cursor-default"
                    style={{ 
                      borderRadius: 16, 
                      border: `1px solid ${config.color}20`,
                      overflow: 'hidden'
                    }}
                    bodyStyle={{ padding: 0 }}
                  >
                    {/* هدر رنگی */}
                    <div 
                      style={{ 
                        background: config.bgColor,
                        padding: '12px 16px',
                        borderBottom: `1px solid ${config.color}20`
                      }}
                      className="dark:bg-opacity-10"
                    >
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: config.color }}
                        >
                          {React.cloneElement(config.icon, { style: { color: '#fff', fontSize: 16 } })}
                        </div>
                        <Text style={{ fontFamily: 'CustomFont', fontSize: 13, fontWeight: 600 }}>
                          {getResourceTypeLabel(type)}
                        </Text>
                      </div>
                    </div>
                    
                    {/* محتوا */}
                    <div style={{ padding: '12px 16px' }}>
                      <div className="flex items-baseline gap-1 mb-1">
                        <Text style={{ fontFamily: 'CustomFont', fontSize: 22, fontWeight: 700, color: config.color }}>
                          {data.total_amount?.toLocaleString('fa-IR') || 0}
                        </Text>
                      </div>
                      <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                        {data.count?.toLocaleString('fa-IR') || 0} مورد ثبت شده
                      </Text>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* لیست منابع */}
        <div className="mt-4">
          {resourcesLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2"
                >
                  <div className="flex items-center gap-3">
                    <Skeleton.Button active size="small" style={{ width: 60, height: 24, borderRadius: 6 }} />
                    <div className="flex flex-col gap-1">
                      <Skeleton.Input active size="small" style={{ width: 100, height: 16 }} />
                      <Skeleton.Input active size="small" style={{ width: 150, height: 12 }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton.Button active size="small" style={{ width: 32, height: 32 }} />
                    <Skeleton.Button active size="small" style={{ width: 32, height: 32 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : resources.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ fontFamily: 'CustomFont' }}>
                  هنوز منبعی ثبت نشده است
                </span>
              }
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
                style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
                className="bg-green-600 hover:bg-green-700 border-0"
              >
                افزودن اولین منبع
              </Button>
            </Empty>
          ) : (
            <div className="space-y-3">
              {resources.map((r) => {
                const dynamicFields = r.dynamic_fields || {};
                const config = getResourceTypeConfig(r.resource_type);
                
                // نمایش اطلاعات بر اساس نوع منبع
                let displayInfo = '';
                if (r.resource_type === 'بودجه') {
                  displayInfo = `${dynamicFields.amount?.toLocaleString('fa-IR') || 0} ${dynamicFields.currency || ''}`;
                } else if (r.resource_type === 'نیروی_انسانی') {
                  displayInfo = `${dynamicFields.person_count || 0} نفر${dynamicFields.hours ? ` - ${dynamicFields.hours} ساعت` : ''}`;
                } else if (r.resource_type === 'تجهیزات') {
                  displayInfo = `${dynamicFields.equipment_name || ''} (${dynamicFields.quantity || 0} عدد)`;
                } else if (r.resource_type === 'ابزار') {
                  displayInfo = `${dynamicFields.tool_name || ''} (${dynamicFields.quantity || 0} عدد)`;
                } else if (r.resource_type === 'پیمانکار') {
                  displayInfo = `${dynamicFields.contractor_name || ''} - ${dynamicFields.contract_value?.toLocaleString('fa-IR') || 0}`;
                } else if (r.resource_type === 'سایر') {
                  displayInfo = r.description || 'منبع سفارشی';
                }
                
                return (
                <div
                  key={r.id}
                  className="p-3 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col gap-2 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: config.bgColor }}
                        >
                          {React.cloneElement(config.icon, { style: { color: config.color, fontSize: 18 } })}
                        </div>
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                      <Tag color="geekblue" style={{ fontFamily: 'CustomFont', margin: 0 }}>
                        {getResourceTypeLabel(r.resource_type)}
                      </Tag>
                            {r.status && (
                              <Tag 
                                color={
                                  r.status === 'completed' ? 'success' :
                                  r.status === 'used' ? 'processing' :
                                  r.status === 'allocated' ? 'warning' : 'default'
                                }
                                style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                              >
                                {r.status === 'planned' ? 'برنامه‌ریزی شده' :
                                 r.status === 'allocated' ? 'تخصیص داده شده' :
                                 r.status === 'used' ? 'استفاده شده' :
                                 r.status === 'completed' ? 'تکمیل شده' : r.status}
                              </Tag>
                            )}
                          </div>
                          <Text style={{ fontFamily: 'CustomFont', marginTop: 4 }}>
                            {displayInfo}
                        </Text>
                        {r.description && (
                          <Text
                            type="secondary"
                              style={{ fontFamily: 'CustomFont', fontSize: 11, marginTop: 2 }}
                          >
                            {r.description}
                          </Text>
                        )}
                      </div>
                    </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEditModal(r.id)}
                        style={{ fontFamily: 'CustomFont' }}
                        />
                      <Popconfirm
                        title="حذف منبع"
                        description="آیا از حذف این منبع مطمئن هستید؟"
                        okText="حذف"
                        cancelText="انصراف"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onDeleteResource(r.id)}
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          style={{ fontFamily: 'CustomFont' }}
                          />
                      </Popconfirm>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* مودال ایجاد/ویرایش منبع */}
      <Modal
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            {editingResourceId ? 'ویرایش منبع' : 'افزودن منبع جدید'}
          </span>
        }
        open={resourceModalOpen}
        onCancel={closeResourceModal}
        width={700}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={closeResourceModal}
              style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
            >
              انصراف
            </Button>
            <Button
              type="primary"
              onClick={onSubmitResource}
              loading={resourceSubmitting}
              className={primaryButtonClass}
              style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
            >
              {editingResourceId ? 'ذخیره' : 'ثبت'}
            </Button>
          </div>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text style={{ fontFamily: 'CustomFont' }} strong>
              نوع منبع (الزامی)
            </Text>
            <Select
              size="large"
              placeholder="انتخاب نوع"
              value={resourceType}
              onChange={(val) => {
                setResourceType(val);
                // Reset all dynamic fields when type changes
                resetAllFields();
              }}
              style={{ width: '100%', marginTop: 8, fontFamily: 'CustomFont' }}
              options={resourceTypeOptions}
            />
          </div>

          {/* فیلدهای پویا بر اساس نوع منبع */}
          {resourceType === 'بودجه' && (
            <>
          <Row gutter={12}>
                <Col xs={24} sm={12}>
              <Text style={{ fontFamily: 'CustomFont' }} strong>
                    مبلغ بودجه (الزامی)
              </Text>
              <InputNumber
                size="large"
                min={0}
                max={999999999999}
                precision={0}
                formatter={numberFormatter}
                parser={numberParser}
                value={budgetAmount}
                onChange={(v) => setBudgetAmount(v || '')}
                style={{ width: '100%', marginTop: 8 }}
                placeholder="مثلاً: 1000000"
              />
            </Col>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }} strong>
                    واحد پول (الزامی)
                  </Text>
              <Input
                size="large"
                    maxLength={50}
                    value={budgetCurrency}
                    onChange={(e) => setBudgetCurrency(e.target.value)}
                placeholder="مثلاً: ریال"
                style={{ marginTop: 8 }}
              />
            </Col>
          </Row>
              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }}>دسته‌بندی بودجه</Text>
                  <Input
                    size="large"
                    maxLength={255}
                    value={budgetCategory}
                    onChange={(e) => setBudgetCategory(e.target.value)}
                    placeholder="مثلاً: عملیاتی"
                    style={{ marginTop: 8 }}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }}>سال مالی</Text>
                  <InputNumber
                    size="large"
                    min={1400}
                    max={1500}
                    precision={0}
                    value={budgetFiscalYear}
                    onChange={(v) => setBudgetFiscalYear(v || '')}
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder="مثلاً: 1403"
                  />
                </Col>
              </Row>
            </>
          )}

          {resourceType === 'نیروی_انسانی' && (
            <>
              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }} strong>
                    تعداد نفرات (الزامی)
                  </Text>
                  <InputNumber
                    size="large"
                    min={1}
                    precision={0}
                    value={personCount}
                    onChange={(v) => setPersonCount(v || '')}
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder="مثلاً: 5"
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }}>تعداد ساعت</Text>
                  <InputNumber
                    size="large"
                    min={0}
                    precision={0}
                    value={personHours}
                    onChange={(v) => setPersonHours(v || '')}
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder="مثلاً: 160"
                  />
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }}>سطح مهارت</Text>
                  <Input
                    size="large"
                    maxLength={100}
                    value={personSkillLevel}
                    onChange={(e) => setPersonSkillLevel(e.target.value)}
                    placeholder="مثلاً: متوسط"
                    style={{ marginTop: 8 }}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }}>موقعیت شغلی</Text>
                  <Input
                    size="large"
                    maxLength={255}
                    value={personPosition}
                    onChange={(e) => setPersonPosition(e.target.value)}
                    placeholder="مثلاً: برنامه‌نویس"
                    style={{ marginTop: 8 }}
                  />
                </Col>
              </Row>
              <div>
                <Text style={{ fontFamily: 'CustomFont' }}>دپارتمان</Text>
                <Input
                  size="large"
                  maxLength={255}
                  value={personDepartment}
                  onChange={(e) => setPersonDepartment(e.target.value)}
                  placeholder="مثلاً: IT"
                  style={{ marginTop: 8 }}
                />
              </div>
            </>
          )}

          {resourceType === 'تجهیزات' && (
            <>
              <div>
                <Text style={{ fontFamily: 'CustomFont' }} strong>
                  نام تجهیزات (الزامی)
                </Text>
                <Input
                  size="large"
                  maxLength={255}
                  value={equipmentName}
                  onChange={(e) => setEquipmentName(e.target.value)}
                  placeholder="مثلاً: سرور HP ProLiant"
                  style={{ marginTop: 8 }}
                />
              </div>
              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }} strong>
                    تعداد (الزامی)
                  </Text>
                  <InputNumber
                    size="large"
                    min={1}
                    precision={0}
                    value={equipmentQuantity}
                    onChange={(v) => setEquipmentQuantity(v || '')}
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder="مثلاً: 2"
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }}>مدل</Text>
                  <Input
                    size="large"
                    maxLength={255}
                    value={equipmentModel}
                    onChange={(e) => setEquipmentModel(e.target.value)}
                    placeholder="مثلاً: DL380 Gen10"
                    style={{ marginTop: 8 }}
                  />
                </Col>
              </Row>
              <div>
                <Text style={{ fontFamily: 'CustomFont' }}>وضعیت</Text>
                <Input
                  size="large"
                  maxLength={100}
                  value={equipmentCondition}
                  onChange={(e) => setEquipmentCondition(e.target.value)}
                  placeholder="مثلاً: نو"
                  style={{ marginTop: 8 }}
                />
              </div>
            </>
          )}

          {resourceType === 'ابزار' && (
            <>
              <div>
                <Text style={{ fontFamily: 'CustomFont' }} strong>
                  نام ابزار (الزامی)
                </Text>
                <Input
                  size="large"
                  maxLength={255}
                  value={toolName}
                  onChange={(e) => setToolName(e.target.value)}
                  placeholder="مثلاً: لپ‌تاپ"
                  style={{ marginTop: 8 }}
                />
              </div>
              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }} strong>
                    تعداد (الزامی)
                  </Text>
                  <InputNumber
                    size="large"
                    min={1}
                    precision={0}
                    value={toolQuantity}
                    onChange={(v) => setToolQuantity(v || '')}
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder="مثلاً: 3"
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }}>وضعیت</Text>
                  <Input
                    size="large"
                    maxLength={100}
                    value={toolCondition}
                    onChange={(e) => setToolCondition(e.target.value)}
                    placeholder="مثلاً: خوب"
                    style={{ marginTop: 8 }}
                  />
                </Col>
              </Row>
            </>
          )}

          {resourceType === 'پیمانکار' && (
            <>
              <div>
                <Text style={{ fontFamily: 'CustomFont' }} strong>
                  نام پیمانکار (الزامی)
                </Text>
                <Input
                  size="large"
                  maxLength={255}
                  value={contractorName}
                  onChange={(e) => setContractorName(e.target.value)}
                  placeholder="مثلاً: شرکت ABC"
                  style={{ marginTop: 8 }}
                />
              </div>
              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }} strong>
                    مبلغ قرارداد (الزامی)
                  </Text>
                  <InputNumber
                    size="large"
                    min={0}
                    max={999999999999}
                    precision={0}
                    formatter={numberFormatter}
                    parser={numberParser}
                    value={contractValue}
                    onChange={(v) => setContractValue(v || '')}
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder="مثلاً: 5000000"
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text style={{ fontFamily: 'CustomFont' }}>نوع قرارداد</Text>
                  <Input
                    size="large"
                    maxLength={255}
                    value={contractType}
                    onChange={(e) => setContractType(e.target.value)}
                    placeholder="مثلاً: ثابت"
                    style={{ marginTop: 8 }}
                  />
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} sm={8}>
                  <Text style={{ fontFamily: 'CustomFont' }}>مدت قرارداد</Text>
                  <InputNumber
                    size="large"
                    min={1}
                    precision={0}
                    value={contractDuration}
                    onChange={(v) => setContractDuration(v || '')}
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder="مثلاً: 6"
                  />
                </Col>
                <Col xs={24} sm={16}>
                  <Text style={{ fontFamily: 'CustomFont' }}>واحد مدت قرارداد</Text>
                  <Select
                    size="large"
                    placeholder="انتخاب واحد"
                    value={contractDurationUnit}
                    onChange={setContractDurationUnit}
                    style={{ width: '100%', marginTop: 8 }}
                    options={[
                      { value: 'روز', label: 'روز' },
                      { value: 'ماه', label: 'ماه' },
                      { value: 'سال', label: 'سال' },
                    ]}
                  />
                </Col>
              </Row>
            </>
          )}

          {resourceType === 'سایر' && (
            <>
              <div>
                <Text style={{ fontFamily: 'CustomFont' }}>فیلدهای سفارشی</Text>
                <div className="space-y-2 mt-2">
                  {customFields.map((cf, index) => (
                    <Row key={index} gutter={8}>
                      <Col xs={24} sm={10}>
                        <Input
                          size="large"
                          maxLength={255}
                          value={cf.key || ''}
                          onChange={(e) => {
                            const newFields = [...customFields];
                            newFields[index] = { ...newFields[index], key: e.target.value };
                            setCustomFields(newFields);
                          }}
                          placeholder="کلید"
                        />
                      </Col>
                      <Col xs={24} sm={12}>
                        <Input
                          size="large"
                          value={cf.value || ''}
                          onChange={(e) => {
                            const newFields = [...customFields];
                            newFields[index] = { ...newFields[index], value: e.target.value };
                            setCustomFields(newFields);
                          }}
                          placeholder="مقدار"
                        />
                      </Col>
                      <Col xs={24} sm={2}>
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            setCustomFields(customFields.filter((_, i) => i !== index));
                          }}
                          style={{ width: '100%' }}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => setCustomFields([...customFields, { key: '', value: '' }])}
                    style={{ width: '100%', borderRadius: 10 }}
                  >
                    افزودن فیلد سفارشی
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* فیلدهای مشترک */}
          {resourceType && (
            <>
          <div>
            <Text style={{ fontFamily: 'CustomFont' }}>توضیحات (اختیاری)</Text>
            <TextArea
              rows={3}
                  maxLength={65535}
              value={resourceDescription}
              onChange={(e) => setResourceDescription(e.target.value)}
              placeholder="توضیحات منبع..."
              style={{ marginTop: 8, borderRadius: 10 }}
            />
          </div>
            </>
          )}
        </Space>
      </Modal>
    </>
  );
}
